import React, { useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { LayoutDashboard, FilePlus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clearCacheOverlaysScreenshots } from '../../../../helper/cacheHelper';
import { IntlMessages } from '../../../../helper/langHelper';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 1rem;
  backdrop-filter: blur(0.2px);
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 10px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: ${slideUp} 0.3s ease-out;
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  color: red;

  &:hover {
    background-color: #f1d7d7;
    color: red;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;

  &:hover {
    background: #f3f4f6;
    border-color: #3b82f6;
    transform: translateY(-2px);
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }
`;

const ButtonIcon = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  border-radius: 10px;
  color: #ffffff;

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ButtonContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ButtonText = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ButtonDescription = styled.span`
  font-size: 0.875rem;
  color: #4b4c4c;
  line-height: 1.4;
`;

// Main Component
const EnquiryModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle option click
  const handleOptionClick = useCallback(
    (path) => {
      clearCacheOverlaysScreenshots();
      onClose();
      navigate(path);
    },
    [navigate, onClose]
  );

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e) => {
      if (overlayRef.current === e.target) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Overlay ref={overlayRef} onClick={handleOverlayClick} isOpen={isOpen}>
      <ModalContainer
        isOpen={isOpen}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <ModalHeader>
          <ModalTitle id="modal-title">
            <IntlMessages id="enquiry.question" />
          </ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close modal" className="modal-close-btn">
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ButtonsContainer>
            <ActionButton
              onClick={() => handleOptionClick('/addressSearch?flow=prelodgement')}
              className="pre_lodgement_button"
            >
              <ButtonIcon>
                <LayoutDashboard />
              </ButtonIcon>
              <ButtonContent>
                <ButtonText>
                  <IntlMessages id="button.label.pre-lodgement" />
                </ButtonText>
                <ButtonDescription>Get planning guidance before you apply.</ButtonDescription>
              </ButtonContent>
            </ActionButton>

            <ActionButton
              onClick={() => handleOptionClick('/addressSearch')}
              className="final_lodgement_button"
            >
              <ButtonIcon>
                <FilePlus />
              </ButtonIcon>
              <ButtonContent>
                <ButtonText>
                  <IntlMessages id="button.lodge.application" />
                </ButtonText>
                <ButtonDescription>Submit your full application for assessment.</ButtonDescription>
              </ButtonContent>
            </ActionButton>
          </ButtonsContainer>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

export default EnquiryModal;
